import { apiClient, ApiResponse } from './api'

export interface UserInfo {
  userId: string
  firstName: string
  lastName: string
  email: string
}

export interface Vulnerability {
  _id: string
  tenantId: string
  projectId: string
  assetId: string
  scanId: string
  name: string
  title?: string
  cvss: number
  sla: number
  synopsis: string
  recommendation: string
  impact: string
  status: string
  severity: string
  reference?: string[]
  pocs?: Array<{
    _id?: string
    type: 'TEXT' | 'IMAGE'
    caption?: string  // Only for TEXT type
    details?: string  // Only for TEXT type
    images?: Array<{
      caption: string
      imageKey: string
    }>
  }>
  createdAt: string
  createdOn?: string
  updatedAt: string
  createdBy?: UserInfo | string
  updatedBy?: UserInfo | string
  deletedAt?: string | null
  deletedBy?: UserInfo | string | null
  published?: boolean
  publishedStatus?: string
  statusChangeHistory?: any[]
  scanType?: string
  id?: string
  __v: number
}

export interface CreateVulnerabilityRequest {
  tenantId: string
  projectId: string
  assetId: string
  scanId: string
  name: string
  cvss: number
  sla: number
  synopsis: string
  recommendation: string
  impact: string
  reference?: string[]
  pocs?: Array<{
    type: 'TEXT' | 'IMAGE'
    caption?: string  // Only for TEXT type
    details?: string  // Only for TEXT type
    images?: Array<{
      caption: string
      imageKey: string
    }>
  }>
}

export interface UpdateVulnerabilityRequest {
  name?: string
  cvss?: number
  sla?: number
  synopsis?: string
  recommendation?: string
  impact?: string
  pocs?: Array<{
    type: 'TEXT' | 'IMAGE'
    caption?: string  // Only for TEXT type
    details?: string  // Only for TEXT type
    images?: Array<{
      caption: string
      imageKey: string
    }>
  }>
  reference?: string[]
  status?: string
  publishedStatus?: string
  published?: boolean
}

export interface GetVulnerabilitiesParams {
  projectId?: string
  assetId?: string
  scanId?: string
  page?: number
  limit?: number
  search?: string
}

export interface SavedVulnerability {
  _id: string
  name: string
  cvss: number
  sla: number
  synopsis: string
  impact: string
  status: string
  severity: string
  reference?: string[]
  createdAt: string
  updatedAt: string
  createdBy?: string
  updatedBy?: string
  deletedAt?: string
  deletedBy?: string
  __v: number
}

export interface SaveVulnerabilityRequest {
  name: string
  cvss: number
  sla: number
  synopsis: string
  impact: string
}

export interface VulnerabilitiesPaginatedResponse {
  success: boolean
  message: string
  data: {
    data: Vulnerability[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
}

export interface UnpublishedVulnerability {
  _id: string
  createdAt: string
  createdBy: UserInfo
  updatedAt: string
  updatedBy: UserInfo
  deletedAt: string | null
  deletedBy: UserInfo | null
  published: boolean
  publishedStatus: string
  tenantId: string
  projectId: string
  assetId: string
  scanId: string
  name: string
  cvss: number
  sla: number
  synopsis: string
  recommendation: string
  impact: string
  status: string
  severity: string
  reference: string[]
  __v: number
  project: {
    _id: string
    name: string
    description: string
    status: string
    tenantId: string
  }
  asset: {
    _id: string
    assetType: string
    name: string
    description: string
    webUrl?: string
  }
  scan: {
    _id: string
    type: string
    status: string
    name: string
    description: string
    testingType: string
  }
}

export interface UnpublishedVulnerabilitiesResponse {
  success: boolean
  message: string
  data: {
    data: UnpublishedVulnerability[]
    total: number
    page: number
    limit: number
  }
}

class VulnerabilitiesApiService {
  async getVulnerabilities(params: GetVulnerabilitiesParams = {}): Promise<ApiResponse<Vulnerability[]>> {
    // If only assetId is provided, use the asset-specific endpoint
    if (params.assetId && !params.projectId && !params.scanId) {
      return this.getVulnerabilitiesByAssetId(params.assetId)
    }

    const queryParams = new URLSearchParams()

    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.assetId) queryParams.append('assetId', params.assetId)
    if (params.scanId) queryParams.append('scanId', params.scanId)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.search) queryParams.append('search', params.search)

    const queryString = queryParams.toString()
    const endpoint = `/projects-service/api/v1/vulnerabilities${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<VulnerabilitiesPaginatedResponse>(endpoint, {}, 'PROJECTS')

    return {
      success: response.success,
      message: response.message,
      data: (response.data as any).data || []
    }
  }

  async getVulnerabilitiesByAssetId(assetId: string): Promise<ApiResponse<Vulnerability[]>> {
    const response = await apiClient.get<VulnerabilitiesPaginatedResponse>(`/projects-service/api/v1/vulnerabilities/${assetId}`, {}, 'PROJECTS')

    return {
      success: response.success,
      message: response.message,
      data: (response.data as any).data || []
    }
  }

  async getVulnerabilitiesByScanId(scanId: string): Promise<ApiResponse<Vulnerability[]>> {
    const response = await apiClient.get<VulnerabilitiesPaginatedResponse>(`/projects-service/api/v1/vulnerabilities/scan/${scanId}`, {}, 'PROJECTS')

    return {
      success: response.success,
      message: response.message,
      data: (response.data as any).data || []
    }
  }

  async getVulnerabilityById(vulnerabilityId: string): Promise<ApiResponse<Vulnerability>> {
    const response = await apiClient.get<Vulnerability>(`/projects-service/api/v1/vulnerabilities/${vulnerabilityId}`, {}, 'PROJECTS')
    return response
  }

  async createVulnerability(data: CreateVulnerabilityRequest): Promise<ApiResponse<Vulnerability>> {
    const response = await apiClient.post<Vulnerability>('/projects-service/api/v1/vulnerabilities', data, {}, 'PROJECTS')
    return response
  }

  async updateVulnerability(vulnerabilityId: string, data: UpdateVulnerabilityRequest): Promise<ApiResponse<Vulnerability>> {
    const response = await apiClient.patch<Vulnerability>(`/projects-service/api/v1/vulnerabilities/${vulnerabilityId}`, data, {}, 'PROJECTS')
    return response
  }

  async deleteVulnerability(vulnerabilityId: string): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<void>(`/projects-service/api/v1/vulnerabilities/${vulnerabilityId}`, {}, 'PROJECTS')
    return response
  }

  async fixVulnerability(vulnerabilityId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    const response = await apiClient.patch<{ success: boolean; message: string }>(`/projects-service/api/v1/vulnerabilities/${vulnerabilityId}/fix`, {}, {}, 'PROJECTS')
    return response
  }

  async markReadyToRevalidate(vulnerabilityId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    const response = await apiClient.patch<{ success: boolean; message: string }>(`/projects-service/api/v1/vulnerabilities/${vulnerabilityId}/mark-ready-to-revalidate`, {}, {}, 'PROJECTS')
    return response
  }

  async uploadImages(tenantId: string, files: File[]): Promise<ApiResponse<{
    images: Array<{
      s3Key: string
      presignedUrl: string
      size: number
      mimetype: string
      originalName: string
    }>
    expiresIn: number
    tenantId: string
    count: number
  }>> {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('images', file)
    })

    const response = await apiClient.postFormData<{
      images: Array<{
        s3Key: string
        presignedUrl: string
        size: number
        mimetype: string
        originalName: string
      }>
      expiresIn: number
      tenantId: string
      count: number
    }>(`/projects-service/api/v1/vulnerabilities/upload/images?tenantId=${tenantId}`, formData, {}, 'PROJECTS')
    
    return response
  }

  async getPresignedUrl(key: string): Promise<ApiResponse<{
    s3Key: string
    presignedUrl: string
    expiresIn: number
  }>> {
    const response = await apiClient.post<{
      s3Key: string
      presignedUrl: string
      expiresIn: number
    }>('/projects-service/api/v1/vulnerabilities/presigned-url', { key }, {}, 'PROJECTS')
    
    return response
  }

  // Saved vulnerabilities (from database collection)
  async getSavedVulnerabilities(params: { page?: number; limit?: number; search?: string } = {}): Promise<ApiResponse<SavedVulnerability[]>> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.search) queryParams.append('search', params.search)

    const queryString = queryParams.toString()
    const endpoint = `/projects-service/api/v1/vulnerabilities${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<VulnerabilitiesPaginatedResponse>(endpoint, {}, 'PROJECTS')

    return {
      success: response.success,
      message: response.message,
      data: (response.data as any).data || []
    }
  }

  async saveVulnerability(data: SaveVulnerabilityRequest): Promise<ApiResponse<SavedVulnerability>> {
    const response = await apiClient.post<SavedVulnerability>('/projects-service/api/v1/vulnerabilities/save', data, {}, 'PROJECTS')
    return response
  }

  // Get vulnerability statistics by scan ID
  async getVulnerabilityStats(scanId: string): Promise<ApiResponse<Array<{ _id: string; count: number }>>> {
    try {
      const response = await apiClient.get<Array<{ _id: string; count: number }>>(`/projects-service/api/v1/vulnerabilities/stats/${scanId}`, {}, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to fetch vulnerability stats:', error)
      throw error
    }
  }

  // Get scan history by scan ID
  async getScanHistory(scanId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get<any>(`/projects-service/api/v1/vulnerabilities/history/${scanId}`, {}, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to fetch scan history:', error)
      throw error
    }
  }

  // Get unpublished vulnerabilities
  async getUnpublishedVulnerabilities(params: {
    page?: number
    limit?: number
    tenantId?: string
    projectId?: string
    assetId?: string
    scanId?: string
    severity?: string
    status?: string
  } = {}): Promise<ApiResponse<{
    data: UnpublishedVulnerability[]
    total: number
    page: number
    limit: number
  }>> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.tenantId) queryParams.append('tenantId', params.tenantId)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.assetId) queryParams.append('assetId', params.assetId)
    if (params.scanId) queryParams.append('scanId', params.scanId)
    if (params.severity) queryParams.append('severity', params.severity)
    if (params.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    const endpoint = `/projects-service/api/v1/vulnerabilities/unpublished/list${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<{
      data: UnpublishedVulnerability[]
      total: number
      page: number
      limit: number
    }>(endpoint, {}, 'PROJECTS')

    return response
  }

  // Update vulnerability published status
  async updatePublishedStatus(
    vulnerabilityId: string, 
    data: { published: boolean; publishedStatus: 'PUBLISHED' | 'REJECTED' | 'UNPUBLISHED' }
  ): Promise<ApiResponse<Vulnerability>> {
    const response = await apiClient.patch<Vulnerability>(
      `/projects-service/api/v1/vulnerabilities/published-status/${vulnerabilityId}`, 
      data, 
      {}, 
      'PROJECTS'
    )
    return response
  }
}

export const vulnerabilitiesApi = new VulnerabilitiesApiService()
export default vulnerabilitiesApi
